import { useEffect, useRef } from 'react';
import { useContextSelector as useCS } from 'use-context-selector';
import { useRequest } from 'domain/core/hooks/useRequest';
import { ApiMethodsEnum } from 'infrastructure/enums/ApiMethodsEnum';
import { TUpdate } from 'domain/types';
import {
  GlobalConfigContext,
  TGlobalConfigContextState,
} from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { showSystemToast } from 'view/components/SystemToast';

export interface IProfileSettingsFormFields {
  firstName: string;
  lastName: string;
  contactEmail: string;
  showEmail: boolean;
  contactPhone: string;
  showPhone: boolean;
  currentLocation: null;
  gender: { id: number; name: string } | null;
  ethnicity: null | { id: number; name: string };
  disabilityStatus: null | { id: number; name: string };
  veteranStatus: null | { id: number; name: string };
}

export const useUpdateProfileSettings = (): any => {
  const globalUpdate: TUpdate<TGlobalConfigContextState> | undefined = useCS(
    GlobalConfigContext,
    globalConfigSelectors.update
  );
  const silentRef = useRef(false);

  const { data, isLoading, status, error, send } = useRequest({
    url: '/api/v1/auth/me',
    method: ApiMethodsEnum.Patch,
    sendOnStart: false,
    body: {},
  });

  const updateProfileSettings = (
    formData: IProfileSettingsFormFields,
    avatarId: string,
    silent = false
  ) => {
    silentRef.current = silent;
    const {
      gender,
      ethnicity,
      disabilityStatus,
      veteranStatus,
      showPhone,
      showEmail,
      firstName,
      lastName,
      contactEmail,
      contactPhone,
      currentLocation,
    } = formData;

    const dataToSend = {
      photo: {
        id: avatarId,
      },
      demographicInfo: {
        gender: gender?.name,
        ethnicity: ethnicity?.name,
        disabilityStatus: disabilityStatus?.name,
        veteranStatus: veteranStatus?.name,
      },
      privacySettings: {
        showPhone,
        showEmail,
      },
      firstName,
      lastName,
      contactEmail,
      contactPhone,
      currentLocation,
    };

    return send(dataToSend);
  };

  useEffect(() => {
    if (!isLoading && !error && data) {
      if (globalUpdate) globalUpdate({ userData: data });
      if (!silentRef.current) {
        showSystemToast('success', 'Profile Updated', 'Your changes have been saved successfully.');
      }
    }
  }, [isLoading, error, data]);

  return {
    isLoading,
    error: error?.response?.data?.errors || null,
    updateProfileSettings,
    status,
  };
};
